import { ApiError } from '@shared/api-utils';
import type { CartSubmitErrorRetryTarget, CheckoutErrorSecondaryAction } from './types';

type CartSubmitErrorScenario = 'insufficient-balance' | 'cart-adjusted' | 'bet-limit' | 'submit-failed';

type CartSubmitErrorCopyGroup = 'insufficient_balance' | 'cart_adjusted' | 'bet_limit' | 'submit_failed';

type CartSubmitErrorTitleKey = `${CartSubmitErrorCopyGroup}.title`;

type CartSubmitErrorMessageKey = `${CartSubmitErrorCopyGroup}.description`;

type MappedCartSubmitError = {
  scenario: CartSubmitErrorScenario;
  titleKey: CartSubmitErrorTitleKey;
  messageKey: CartSubmitErrorMessageKey;
  retryTarget: CartSubmitErrorRetryTarget;
  secondaryAction?: CheckoutErrorSecondaryAction;
};

type CartSubmitErrorDefinition = {
  scenario: CartSubmitErrorScenario;
  copyGroup: CartSubmitErrorCopyGroup;
  retryTarget: CartSubmitErrorRetryTarget;
  secondaryAction?: CheckoutErrorSecondaryAction;
};

const SUBMIT_FAILED: CartSubmitErrorDefinition = { scenario: 'submit-failed', copyGroup: 'submit_failed', retryTarget: 'submit-cart' };

const DEFINITIONS_BY_STATUS: Record<number, CartSubmitErrorDefinition> = {
  402: { scenario: 'insufficient-balance', copyGroup: 'insufficient_balance', retryTarget: 'summary' },
  409: { scenario: 'cart-adjusted', copyGroup: 'cart_adjusted', retryTarget: 'submit-cart' },
  // Bet limits are set by regulation or by the player's safer gambling settings and
  // cannot be raised in the app, so support is the only way forward.
  422: { scenario: 'bet-limit', copyGroup: 'bet_limit', retryTarget: 'none', secondaryAction: 'contact-support' },
  500: SUBMIT_FAILED,
};

function mapCartSubmitError(error: unknown): MappedCartSubmitError {
  const status = error instanceof ApiError ? error.getStatus() : undefined;
  const { scenario, copyGroup, retryTarget, secondaryAction } = (status === undefined ? undefined : DEFINITIONS_BY_STATUS[status]) ?? SUBMIT_FAILED;

  return {
    scenario,
    titleKey: `${copyGroup}.title`,
    messageKey: `${copyGroup}.description`,
    retryTarget,
    ...(secondaryAction ? { secondaryAction } : {}),
  };
}

export { mapCartSubmitError };
export type { CartSubmitErrorMessageKey, CartSubmitErrorScenario, CartSubmitErrorTitleKey, MappedCartSubmitError };
